import { useState, useEffect, useContext, createContext, useRef, useMemo, useCallback } from "react";
import { baseURL } from "../lib";
import axios from 'axios'
import { useNavigate } from "react-router-dom";

const AuthContext = createContext()

const AuthProvider = ({ children }) => {

    const [restaurantId, setRestaurantId] = useState(null)
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const [auth, setAuth] = useState({
        user: null,
        token: ""
    })
    const navigate = useNavigate()

    const restaurantPermissions = useMemo(() => {
        if (auth?.user?.is_super_admin) return auth?.user?.permissions;
        if (!restaurantId || !auth.user?.permissions) return [];
        const restaurant = auth.user.permissions.find(
            (p) => p.restaurant_id === restaurantId
        );
        return restaurant?.permissions || [];
    }, [restaurantId, auth.user]);

    const headers = {
        Authorization: auth?.token,
        'restaurant-id': restaurantId,
        'restaurant-permissions': JSON.stringify(restaurantPermissions),
    };

    const hasPermission = useCallback((module, method) => {
        if (auth.user?.is_super_admin)
            return true;

        const restaurantPermissions = auth?.user?.permissions?.find(
            (entry) => entry.restaurant_id === restaurantId
        );

        return restaurantPermissions?.permissions?.some(
            (p) => p.module === module && p.method === method
        );
    }, [auth, restaurantId]);

    const value = useMemo(() => ({ ...auth, hasPermission }), [auth, hasPermission, restaurantId]);

    const toast = useRef(null);

    const logout = () => {
        try {
            const data = localStorage.getItem('auth')
            if (data) {
                localStorage.removeItem("auth")
                setAuth({
                    user: null,
                    token: ""
                })
                setRestaurantId(null);
                setIsLoggedIn(false)
            }
        } catch (error) {
            console.log(error);
        }
    };

    axios.defaults.headers.common["Authorization"] = auth?.token

    const login = async (credential) => {
        try {
            const { data } = await axios.post(`${baseURL}/user/login`, credential);

            if (data.error === false) {
                setIsLoggedIn(true)
                setTimeout(function () {
                    toast.current?.show({ severity: 'success', summary: data.data.user.user_name, detail: data.message, life: 3000 })
                }, 500);
                setAuth({
                    ...auth,
                    user: data.data.user,
                    token: data.data.token
                })
                localStorage.setItem('auth', JSON.stringify(data.data))
            } else {
                toast.current?.show({ severity: 'error', summary: 'Login', detail: data.message, life: 3000 })
            }
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    if (errors.length > 1) {
                        toast.current?.show({ severity: 'error', summary: 'Login', detail: "Please fill all mandatory fields.", life: 3000 })
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Login', detail: errors[0].msg, life: 3000 })
                    }
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Login', detail: 'An error occurred. Please try again later.', life: 3000 })
            }
        }
    };

    const loginByPin = async (credential) => {
        try {
            const { data } = await axios.post(`${baseURL}/user/login-by-pin`, credential);

            if (data.error === false) {
                setIsLoggedIn(true)
                setTimeout(function () {
                    toast.current?.show({ severity: 'success', summary: data.data.user.user_name, detail: data.message, life: 3000 })
                }, 500);
                setAuth({
                    ...auth,
                    user: data.data.user,
                    token: data.data.token
                })
                localStorage.setItem('auth', JSON.stringify(data.data))
            } else {
                toast.current?.show({ severity: 'error', summary: 'Login', detail: data.message, life: 3000 })
            }
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    if (errors.length > 1) {
                        toast.current?.show({ severity: 'error', summary: 'Login', detail: "Please fill all mandatory fields.", life: 3000 })
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Login', detail: errors[0].msg, life: 3000 })
                    }
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Login', detail: 'An error occurred. Please try again later.', life: 3000 })
            }
        }
    };

    useEffect(() => {
        const data = localStorage.getItem('auth')
        if (data) {
            const parseData = JSON.parse(data)
            setAuth({
                user: parseData.user,
                token: parseData.token
            })
        }

        axios.defaults.headers.common["Authorization"] = auth?.token;

        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response && error.response.status === 401) {
                    logout();
                    navigate("/");
                }
                return Promise.reject(error);
            }
        );

        return () => axios.interceptors.response.eject(interceptor);
    }, [auth.token])

    return (
        <AuthContext.Provider value={{ auth, setAuth, login, loginByPin, logout, isLoggedIn, toast, restaurantId, setRestaurantId, hasPermission, headers }}>
            {children}
        </AuthContext.Provider>
    )
}

const useAuth = () => useContext(AuthContext)

export { useAuth, AuthProvider }